import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TaxonomyApiService } from '../../../services/api-services/taxonomy-api.service';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { Router } from '@angular/router';
import { NotificationService } from '../../../services/notification.service';
import { ActionService } from '../../../services/action.service';
import { take, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface QueueGroup {
  name: string;
  selected: boolean;
  isLocked?: boolean;
  taxonomyName?: string;
}

interface FilteredQueueGroups {
  selected: QueueGroup[];
  unselected: QueueGroup[];
  locked: QueueGroup[];
}

interface TaxonomyData {
  name: string;
  description: string;
  queueGroups: string[];
  attachedQueues: { [key: string]: string }[];
  availableQueues: {
    agent_to_agent_queues_en: string[];
    agent_to_customer_queues_en: string[];
  };
}

// Static list of queue groups
const STATIC_QUEUE_GROUPS = [
        "AC_T2_LEAD RESOLUTIONS_EN",
        "AC_T2_LEADS ACCESSIBILITY_EN",
        "AC_T2_LEADS CX_EN",
        "AC_T2_LEADS DISRUPTIONS_EN",
        "AC_T2_LEADS GROUPS_EN",
        "AC_T2_LEADS RATES_EN",
        "AC_T2_LEADS SM_EN",
        "AC_T2_LEADS TPA_EN",
        "AC_T2_LEADS YSJ_EN",
        "AC_T2_LEADS_YUL_EN",
        "AC_T2_LEADS_YYZ_EN",
        "AC_T2_RATES HELPDESK_EN",
        "AC_T2_RESOLUTIONS_EN",
        "AC_T2_RESOLUTIONS_TPA_EN",
        "AC_T2_RESOLUTIONS_YSJ_EN",
        "AC_T2_RESOLUTIONS_YUL_EN",
        "AC_T2_RESOLUTIONS_YYZ_EN",
        "AC_T2_TPA HELPDESK_EN",
        "AE_T2_HELPDESK_EN",
        "AE_T2_LEADS_EN",
        "AE_T2_RESOLUTIONS_EN",
        "AE_T1_EXISTING BOOKINGS_EN",
        "AE_RESERVATIONS_TRANSFER_EN",
        "AC_T1_EMERGENCY RESPONSE TEC_EN",
        "AC_T2_SOCIAL MEDIA_EN",
        "AE_T2_NEW BOOKINGS_EN",
        "AC_T2_GROUPS_EN",
        "AC_T2_ACCESSIBILITY_EN",
        "AC_T5_ICN_LEAD_EN",
        "AC_T3_SUPPORT_EN",
        "CR_T5_CUSTOMER RELATIONS_EN",
        "AC_T2_SUPPORT_EN",
        "AC_T4_EXISTING BOOKINGS_EN",
        "AE_T2_SUPPORT_EN",
        "AC_T2_CREW SKED_EN",
        "AC_T4_DISRUPTIONS_EN",
        "AC_T4_P2P_TRANSFER_EN",
        "AC_T1_EXISTING BOOKINGS_EN",
        "AC_T4_ACB_EN",
        "AE_T4_INFORMATION_EN",
        "AC_T2_AGENCY_EN",
        "AE_T1_NEW BOOKINGS_EN",
        "AC_T4_CONVENTIONS_EN",
        "AC_T5_ICN_KOREA_EN",
        "BC_T5_BAGGAGE CLAIMS_EN",
        "AC_T4_ASIA DESK_EN",
        "AC_T5_BOG_NEW_INFO_EN",
        "AE_T3_NEW BOOKINGS_EN",
        "AC_T4_INFORMATION_EN",
        "AC_T5_NEW BOOKINGS_EN",
        "AC_T5_FRA GERMANY_EN",
        "CC_IN_OPS TPA_EN",
        "AE_T2_INFORMATION_EN",
        "CR_T2_PROCESSING_EN",
        "AC_T1_CHAT_CONCIERGE_EN",
        "AC_AEROPLAN_TRANSFER_EN",
        "AC_T2_NEW BOOKINGS_EN",
        "AC_T4_OMW_EN",
        "AE_T5_INFORMATION_EN",
        "AC_T5_BOG_COLOMBIA_EN",
        "AC_T2_CONCIERGE SUPPORT_EN",
        "AE_T2_EXISTING BOOKINGS_EN",
        "AC_T2_A++ SUPPORT_EN",
        "AC_T2_EMPLOYEE BUSINESS_EN",
        "AC_T4_FLIGHT PASS_EN",
        "AE_T3_SUPPORT_EN",
        "AC_T5_ASIA DESK_EN",
        "AC_T2_SPECIAL SUPPORT_EN",
        "AC_T2_AIRPORT SUPPORT_EN",
        "AC_T2_PRIORITY SUPPORT_EN",
        "AC_T5_TYO JAPAN_EN",
        "AC_T4_AGENCY_EN",
        "AC_T4_COMPANION_EN",
        "AC_T1_NOSU_EN",
        "AC_T2_GROUPS OPS_EN",
        "AC_T5_EXISTING BOOKINGS_EN",
        "AC_T5_DLC_CHINA_NEW_INFO_EN",
        "AC_T4_SUPPORT_EN",
        "AC_T1_NEW BOOKINGS_EN",
        "AC_T2_EXISTING BOOKINGS_EN",
        "AC_T5_DLC_CHINA_EN",
        "AC_T1_INFORMATION_EN",
        "AC_T1_VIP_EN",
        "AE_T2_MULTICHANNEL_EN",
        "AC_T3_AEOVERNIGHT_EN",
        "AC_T2_OFFLOAD_EN",
        "AC_T2_INFORMATION_EN",
        "AC_T5_INFORMATION_EN",
        "AC_T5_SUPPORT_EN",
        "AC_T4_NEW BOOKINGS_EN",
        "AC_T1_CONCIERGE_EN",
        "AC_T4_ACCESSIBILITY_EN",
        "AC_T3_INFORMATION_EN",
        "AC_T3_EXISTING BOOKINGS_EN",
        "AE_T5_SUPPORT_EN",
        "AE_T5_EXISTING BOOKINGS_EN",
        "AE_T4_EXISTING BOOKINGS_EN",
        "AC_T2_ACCESSIBILITY HOTLINE_EN",
        "AC_T5_ICN_NEW_INFO_EN",
        "AE_T5_NEW BOOKINGS_EN",
        "AC_T2_AGENCY PREFERRED TPA_EN",
        "AC_T5_AGENCY_EN",
        "AE_T1_SUPPORT_EN",
        "AE_T4_SUPPORT_EN",
        "AC_T1_LIVE ORGANS_EN",
        "AE_T3_INFORMATION_EN",
        "CR_T2_LEADS_EN",
        "AE_T4_NEW BOOKINGS_EN",
        "BC_T2_LEADS_EN",
        "AC_T1_CONCIERGEEXTD_EN",
        "AC_T1_SUPPORT_EN",
        "AE_T3_EXISTING BOOKINGS_EN",
        "AC_T5_EMPLOYEE TRAVEL_EN",
        "AC_T3_NEW BOOKINGS_EN",
        "AE_T1_INFORMATION_EN",
        "AE_OB_TRAVEL OPS",
        "AC_OB_GENRES",
        "AC_OB_BOG",
        "AC_OB_ICN",
        "AE_OB_PHONES",
        "AC_OB_CX",
        "AC_OB_DLC",
        "CR_OB_GENERAL",
        "AE_OB_PHONES OJT1",
        "AC_OB_RATES",
        "AE_OB_LEADS",
        "AC_OB_ACCESSIBILITY",
        "AC_OB_TYO",
        "AC_OB_SPD",
        "BC_OB_GENERAL",
        "AC_OB_CONCIERGE",
        "AC_OB_FRA",
        "AE_OB_PHONES OJT2",
        "CC_OB_MGMT",
        "AC_OB_LEADS",
        "AC_OB_GROUPS",
        "AE_OB_MULTICHANNEL",
        "AC_OB_SOCIAL"
    ];

@Component({
  selector: 'app-taxonomy-configure',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
  ],
  templateUrl: './taxonomy-configure.component.html',
  styleUrl: './taxonomy-configure.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxonomyConfigureComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private http = inject(HttpClient);

  // Form state
  taxonomyId: string = '';
  taxonomy: TaxonomyData = {
    name: '',
    description: '',
    queueGroups: [],
    attachedQueues: [],
    availableQueues: {
      agent_to_agent_queues_en: [],
      agent_to_customer_queues_en: [],
    },
  };
  originalTaxonomy: TaxonomyData = {
    name: '',
    description: '',
    queueGroups: [],
    attachedQueues: [],
    availableQueues: {
      agent_to_agent_queues_en: [],
      agent_to_customer_queues_en: [],
    },
  };
  queueGroups = new FormControl<string[]>([]);
  searchText: string = '';
  deleteInput: string = '';

  // Queue groups state
  queueGroupsList: QueueGroup[] = [];
  selectedGroups: string[] = [];
  filteredQueueGroups: FilteredQueueGroups = { selected: [], unselected: [], locked: [] };

  // UI state
  showConfirmationPopup = false;
  showDeleteConfirmationPopup = false;

  constructor(
    private taxonomyApiService: TaxonomyApiService,
    public taxonomyService: TaxonomyService,
    private router: Router,
    public notificationService: NotificationService,
    private actionService: ActionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.taxonomyId = localStorage.getItem('configureTaxonomyId') || '';
    if (!this.taxonomyId) {
      this.handleMissingTaxonomyId();
      return;
    }

    this.loadTaxonomyData();
  }

  private handleMissingTaxonomyId(): void {
    // this.notificationService.error('Configuration', 'Taxonomy ID not found');
    this.actionService.closeConfigureForm();
  }

  private loadTaxonomyData(): void {
    const localData = JSON.parse(localStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');

    const body = { taxonomy_id: this.taxonomyId, record_id: local_taxonomy_version };

    this.taxonomyApiService
      .getSpecificTaxonomyV2ById(body)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const taxonomyData = response.data;

          // Handle empty or undefined arrays
          const linkedQueueGroups = Array.isArray(taxonomyData.LinkedQueueGroups) ? taxonomyData.LinkedQueueGroups : [];
          const attachedQueues = Array.isArray(taxonomyData.attached_queues) ? taxonomyData.attached_queues : [];
          const availableQueues = taxonomyData.available_queues?.[0] || {
            agent_to_agent_queues_en: [],
            agent_to_customer_queues_en: [],
          };

          // Set selected groups from LinkedQueueGroups
          this.selectedGroups = linkedQueueGroups;
          this.queueGroups.setValue(linkedQueueGroups);

          this.taxonomy = {
            name: taxonomyData.TaxonomyName?.trim() || '',
            description: taxonomyData.TaxonomyDescription?.trim() || '',
            queueGroups: linkedQueueGroups,
            attachedQueues: attachedQueues,
            availableQueues: {
              agent_to_agent_queues_en: Array.isArray(availableQueues.agent_to_agent_queues_en)
                ? availableQueues.agent_to_agent_queues_en
                : [],
              agent_to_customer_queues_en: Array.isArray(availableQueues.agent_to_customer_queues_en)
                ? availableQueues.agent_to_customer_queues_en
                : [],
            },
          };

          this.originalTaxonomy = {
            name: taxonomyData.TaxonomyName?.trim() || '',
            description: taxonomyData.TaxonomyDescription?.trim() || '',
            queueGroups: [...linkedQueueGroups],
            attachedQueues: [...attachedQueues],
            availableQueues: {
              agent_to_agent_queues_en: [
                ...(Array.isArray(availableQueues.agent_to_agent_queues_en)
                  ? availableQueues.agent_to_agent_queues_en
                  : []),
              ],
              agent_to_customer_queues_en: [
                ...(Array.isArray(availableQueues.agent_to_customer_queues_en)
                  ? availableQueues.agent_to_customer_queues_en
                  : []),
              ],
            },
          };

          this.initializeQueueGroups();
          this.filterQueueGroups();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to fetch data', err);
          this.notificationService.error('fetch data', 'Failed to load taxonomy data');
        },
      });
  }

  private initializeQueueGroups(): void {
    // Get LinkedQueueGroups
    const linkedQueueGroups = this.taxonomy.queueGroups || [];

    // Initialize available queues and include LinkedQueueGroups
    const availableQueues = [
      ...linkedQueueGroups, // Add LinkedQueueGroups first
      ...(Array.isArray(this.taxonomy.availableQueues.agent_to_agent_queues_en)
        ? this.taxonomy.availableQueues.agent_to_agent_queues_en
        : []),
      ...(Array.isArray(this.taxonomy.availableQueues.agent_to_customer_queues_en)
        ? this.taxonomy.availableQueues.agent_to_customer_queues_en
        : []),
    ];

    // Remove duplicates while preserving order
    const uniqueAvailableQueues = Array.from(new Set(availableQueues));

    // Create queue groups for available queues and mark LinkedQueueGroups as selected
    this.queueGroupsList = uniqueAvailableQueues.map((group: string) => ({
      name: group,
      selected: linkedQueueGroups.includes(group), // Mark LinkedQueueGroups as selected
      isLocked: false,
    }));

    // Add locked queues from attached_queues
    if (Array.isArray(this.taxonomy.attachedQueues)) {
      this.taxonomy.attachedQueues.forEach((queueObj) => {
        if (queueObj && typeof queueObj === 'object') {
          Object.entries(queueObj).forEach(([queueName, taxonomyName]) => {
            if (queueName && taxonomyName) {
              // Only add if not already in the list
              if (!uniqueAvailableQueues.includes(queueName)) {
                this.queueGroupsList.push({
                  name: queueName,
                  selected: false,
                  isLocked: true,
                  taxonomyName: taxonomyName as string,
                });
              }
            }
          });
        }
      });
    }

    // Set the form control value to match the selected groups
    this.queueGroups.setValue(linkedQueueGroups);
    this.selectedGroups = [...linkedQueueGroups];
  }

  // UI Event Handlers
  handleClosePress(): void {
    localStorage.removeItem('configureTaxonomyId');
    this.actionService.closeConfigureForm();
  }

  handleDeletePress(): void {
    this.showDeleteConfirmationPopup = true;
  }

  handleSaveChanges(): void {
    this.showConfirmationPopup = true;
  }

  cancelSave(): void {
    this.showConfirmationPopup = false;
  }

  cancelDelete(): void {
    this.showDeleteConfirmationPopup = false;
    this.deleteInput = '';
  }

  // Form Event Handlers
  onNameChange(value: string): void {
    this.taxonomy.name = value?.trim() || '';
    this.cdr.detectChanges();
  }

  onDescriptionChange(value: string): void {
    this.taxonomy.description = value?.trim() || '';
    this.cdr.detectChanges();
  }

  onDropdownToggle(opened: boolean): void {
    if (!opened) {
      this.searchText = '';
    }
    this.filterQueueGroups();
  }

  onGroupSelectChange(group: QueueGroup, event: Event): void {
    if (group.isLocked) return;
    event.stopPropagation();

    // Update selection
    if (group.selected) {
      // Remove from selection
      this.taxonomy.queueGroups = this.taxonomy.queueGroups.filter((name) => name !== group.name);
    } else {
      // Add to selection
      this.taxonomy.queueGroups = [...this.taxonomy.queueGroups, group.name];
    }

    group.selected = !group.selected;
    this.queueGroups.setValue(this.taxonomy.queueGroups);
    this.selectedGroups = [...this.taxonomy.queueGroups];
    this.updateSelections();
  }

  deselectOption(groupName: string): void {
    const group = this.queueGroupsList.find((g) => g.name === groupName);
    if (group && !group.isLocked) {
      group.selected = false;
      // Remove from taxonomy.queueGroups
      this.taxonomy.queueGroups = this.taxonomy.queueGroups.filter((name) => name !== groupName);
      // Update form control value
      this.queueGroups.setValue(this.taxonomy.queueGroups);
      // Update selectedGroups
      this.selectedGroups = this.selectedGroups.filter((name) => name !== groupName);
      this.updateSelections();
    }
  }

  // Data Update Methods
  private updateSelections(): void {
    // Update the selected state of all queue groups
    this.queueGroupsList.forEach((group) => {
      if (!group.isLocked) {
        group.selected = this.taxonomy.queueGroups.includes(group.name);
      }
    });

    this.filterQueueGroups();
    this.cdr.detectChanges();
  }

  filterQueueGroups(): void {
    const search = this.searchText.toLowerCase();

    // Ensure queueGroupsList is an array
    if (!Array.isArray(this.queueGroupsList)) {
      this.queueGroupsList = [];
    }

    // Use taxonomy.queueGroups (LinkedQueueGroups) for filtering
    const selectedQueueNames = this.taxonomy.queueGroups;

    this.filteredQueueGroups = {
      selected: this.queueGroupsList.filter(
        (g) => g && selectedQueueNames.includes(g.name) && !g.isLocked && g.name.toLowerCase().includes(search)
      ),
      unselected: this.queueGroupsList.filter(
        (g) => g && !selectedQueueNames.includes(g.name) && !g.isLocked && g.name.toLowerCase().includes(search)
      ),
      locked: this.queueGroupsList.filter((g) => g && g.isLocked && g.name.toLowerCase().includes(search)),
    };
  }

  // API Call Methods
  confirmSaveChanges(): void {
    const payload = {
      taxonomy_name: this.taxonomy.name,
      taxonomy_id: this.taxonomyId,
      taxonomy_description: this.taxonomy.description || '',
      linked_queue_groups: this.queueGroups.value ?? [],
    };

    this.taxonomyApiService.updateTaxonomyV2(payload).subscribe({
      next: () => {
        this.notificationService.success('update taxonomy', 'Taxonomy Updated');
        this.showConfirmationPopup = false;
        this.taxonomyService.loadTaxonomy();
        localStorage.removeItem('configureTaxonomyId');
        this.actionService.closeConfigureForm();
        this.router.navigate(['/taxonomy-dashboard']);
      },
      error: (err) => {
        console.error('Failed to update taxonomy', err);
        this.notificationService.error('update taxonomy', 'Taxonomy Updation Failed');
        this.showConfirmationPopup = false;
      },
    });
  }

  handleDeleteTaxonomy(): void {
    this.taxonomyApiService.deleteTaxonomyV2({ taxonomy_id: this.taxonomyId }).subscribe({
      next: () => {
        this.notificationService.success('delete taxonomy', 'Taxonomy Deleted');
        this.taxonomyService.removeTaxonomyFromStorage(this.taxonomyId);
        this.taxonomyService.loadTaxonomy();
        localStorage.removeItem('configureTaxonomyId');
        this.actionService.closeConfigureForm();
        this.router.navigate(['/taxonomy-dashboard']);
      },
      error: (err) => {
        console.error('Failed to delete taxonomy', err);
        this.notificationService.error('delete taxonomy', 'Deletion Failed');
      },
    });
    this.showDeleteConfirmationPopup = false;
  }

  handleCancel(): void {
    this.taxonomy = { ...this.originalTaxonomy };
    this.selectedGroups = [...this.originalTaxonomy.queueGroups];
    this.queueGroups.setValue([...this.originalTaxonomy.queueGroups]);

    this.queueGroupsList.forEach((group) => {
      if (!group.isLocked) {
        group.selected = this.originalTaxonomy.queueGroups.includes(group.name);
      }
    });

    this.filterQueueGroups();
  }

  // Validation Methods
  isSaveEnabled(): boolean {
    const nameTrimmed = this.taxonomy.name?.trim();
    if (!nameTrimmed) {
      return false;
    }

    const descTrimmed = this.taxonomy.description?.trim() ?? '';
    const originalDescTrimmed = this.originalTaxonomy.description?.trim() ?? '';

    const currentGroups = this.selectedGroups || [];
    const originalGroups = this.originalTaxonomy.queueGroups || [];

    const nameChanged = nameTrimmed !== this.originalTaxonomy.name;
    const descChanged = descTrimmed !== originalDescTrimmed;
    const groupsChanged = JSON.stringify([...currentGroups].sort()) !== JSON.stringify([...originalGroups].sort());

    return nameChanged || descChanged || groupsChanged;
  }

  isDeleteEnabled(): boolean {
    return this.deleteInput.trim() === this.taxonomy.name;
  }
}
